import { N, round, ceil } from './helpers.mjs';

export const GENERAL = [
  // ====================== GENERAL BUILDERS ======================
  {
    slug: 'skip-size',
    trade: 'general-builders',
    name: 'Skip Size Estimator',
    blurb: 'Pick your waste type and size the skip by area × depth, bin bags or rubble bags. Heavy waste (soil, hardcore, concrete) is weight-capped to a smaller skip. Sizes: 2, 4, 6, 8, 10 & 12 yd³.',
    inputs: [
      { key: 'wasteType', label: 'Waste type', type: 'select', default: 'general',
        options: [
          { value: 'general', label: 'General / mixed waste (light)' },
          { value: 'green', label: 'Green / garden waste (light)' },
          { value: 'wood', label: 'Wood / timber (light)' },
          { value: 'soil', label: 'Soil & turf (heavy)' },
          { value: 'hardcore', label: 'Hardcore / rubble / brick (heavy)' },
          { value: 'concrete', label: 'Concrete (very heavy)' },
        ] },
      { key: 'method', label: 'Estimate by', type: 'select', default: 'area',
        options: [
          { value: 'area', label: 'Area × depth' },
          { value: 'binbags', label: 'Bin bags (≈ 50 L each)' },
          { value: 'rubblebags', label: 'Rubble bags (≈ 25 L each)' },
          { value: 'volume', label: 'Volume directly (m³)' },
        ] },
      { key: 'area', label: 'Area — area method', unit: 'm²', placeholder: '10' },
      { key: 'depth', label: 'Depth — area method', unit: 'cm', placeholder: '10', default: 10 },
      { key: 'bags', label: 'Number of bags — bag methods', unit: '', placeholder: '40' },
      { key: 'volumeM3', label: 'Volume — volume method', unit: 'm³', placeholder: '3' },
    ],
    compute: (v) => {
      const method = v.method || 'area';
      let m3 = 0;
      if (method === 'area') m3 = N(v.area) * (N(v.depth) / 100);
      else if (method === 'binbags') m3 = N(v.bags) * 0.05;
      else if (method === 'rubblebags') m3 = N(v.bags) * 0.025;
      else m3 = N(v.volumeM3);
      const yd3 = m3 / 0.7646; // 1 yd³ = 0.7646 m³
      const density = { general: 0.2, green: 0.15, wood: 0.18, soil: 1.5, hardcore: 1.6, concrete: 2.3 }[v.wasteType || 'general'];
      const weightT = m3 * density;
      const SIZES = [2, 4, 6, 8, 10, 12];
      const byVolumeYd = SIZES.find((s) => yd3 <= s) || 12;
      let capYd = 12;
      if (density >= 1.0) {
        if (weightT <= 2) capYd = 2;
        else if (weightT <= 4) capYd = 4;
        else if (weightT <= 6) capYd = 6;
        else capYd = 8; // don't recommend >8yd for heavy muck
      }
      const recYd = Math.min(byVolumeYd, capYd);
      const names = { 2: 'Mini (2yd³)', 4: 'Midi (4yd³)', 6: 'Builder (6yd³)', 8: 'Builder (8yd³)', 10: 'Maxi (10yd³)', 12: 'Maxi (12yd³)' };
      const out = [
        { label: 'Estimated volume', value: round(m3, 2), unit: 'm³' },
        { label: 'Estimated volume', value: round(yd3, 1), unit: 'yd³' },
        { label: 'Estimated weight', value: round(weightT, 2), unit: 'tonnes' },
        { label: 'Recommended skip', value: names[recYd] || `${recYd}yd³`, unit: '' },
      ];
      if (density >= 1.0 && recYd < byVolumeYd) {
        out.push({ label: 'Note', value: 'Heavy waste capped by weight — you may need multiple skips or a grab lorry', unit: '' });
      }
      return out;
    },
  },
  {
    slug: 'wastage-uplift',
    trade: 'general-builders',
    name: 'Wastage Uplift',
    blurb: 'Add wastage % to any quantity.',
    inputs: [
      { key: 'qty', label: 'Quantity', unit: '', placeholder: '100' },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '10', default: 10 },
    ],
    compute: (v) => [
      { label: 'Total with wastage', value: round(N(v.qty) * (1 + N(v.wastage) / 100), 2), unit: '' },
    ],
  },
];
